import { useCallback, useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { FilterField } from '../FilterField'
import {
  FilterState,
  createDefaultFilterState,
} from '@/utilities/types/shared-filter'
import type {
  FilterConfig,
  UpdateDateRangeParams,
} from '@/utilities/types/shared-filter'
import { UserFilterField } from '../filter-user/UserFilterField'
import { useTheme, useMediaQuery } from '@mui/material'

type AlertSeverity = 'success' | 'error' | 'warning' | 'info'

interface FilterTicketModalProps<T = any> {
  open: boolean
  onClose: () => void
  config: FilterConfig<T>
  onApplyFilter: (filters: FilterState<T>) => void
  initialFilters: FilterState<T>
  tab?: string
  handleAlertMessage: (message: string, severity: AlertSeverity) => void
  handleReset: () => void
  handleFilterTicketDates: (
    startCreatedDate: string,
    endCreatedDate: string,
    startModifiedDate: string,
    endModifiedDate: string,
  ) => void
  handleFilterTicketMethodRepair: (method: string) => void
  handleFilterTicketTypeConcern: (typeConcern: string) => void
  handleFilterTicketStatus: (status: string) => void
}

export function FilterTicketsModal<T>({
  open,
  onClose,
  config,
  onApplyFilter,
  initialFilters,
  tab,
  handleAlertMessage,
  handleReset,
  handleFilterTicketDates,
  handleFilterTicketMethodRepair,
  handleFilterTicketTypeConcern,
  handleFilterTicketStatus,
}: FilterTicketModalProps<T>) {
  const [currentFilters, setCurrentFilters] = useState(() => ({
    ...createDefaultFilterState<T>(),
    ...initialFilters,
  }))

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // const handleUpdateFilter = useCallback((field: string, value: string[]) => {
  //   setCurrentFilters((prev) => {
  //     const updatedFilters = {
  //       ...prev,
  //       regularFilters: {
  //         ...prev.regularFilters,
  //         [field]: value,
  //       },
  //     }
  //     return updatedFilters
  //   })
  // }, [])

  const handleUpdateFilter = useCallback((field: string, value: string[]) => {
    console.log(`Updating filter: ${field} with value:`, value)

    setCurrentFilters((prev) => {
      const updatedFilters = {
        ...prev,
        regularFilters: {
          ...prev.regularFilters,
          [field]: value,
        },
      }

      console.log('Updated Filters:', updatedFilters)
      return updatedFilters
    })
  }, [])

  const handleDateRangeChange = useCallback(
    ({ field, range }: UpdateDateRangeParams) => {
      console.log('field and range ', field, range)
      setCurrentFilters((prev) => ({
        ...prev,
        dateRanges: {
          ...prev.dateRanges,
          [field]: range,
        },
      }))
    },
    [],
  )
  const handleApply = useCallback(() => {
    const { createdDate, modifiedDate } = currentFilters.dateRanges
    const { status, methodOfRepair, typeOfConcern } =
      currentFilters.regularFilters

    // Check if created date start is greater than end
    if (
      createdDate?.start &&
      createdDate?.end &&
      createdDate.start > createdDate.end
    ) {
      handleAlertMessage(
        'Created date start cannot be greater than end date.',
        'error',
      )
      return
    }

    // Check if modified date start is greater than end
    if (
      modifiedDate?.start &&
      modifiedDate?.end &&
      modifiedDate.start > modifiedDate.end
    ) {
      handleAlertMessage(
        'Modified date start cannot be greater than end date.',
        'error',
      )
      return
    }

    // Check if modified date is earlier than created date
    if (
      createdDate?.start &&
      modifiedDate?.start &&
      modifiedDate.start < createdDate.start
    ) {
      handleAlertMessage(
        'Modified date cannot be earlier than created date.',
        'error',
      )
      return
    }

    // Apply filters for created and modified dates if valid
    if (createdDate?.start && createdDate?.end) {
      const startCreatedDate = createdDate.start || ''
      const endCreatedDate = createdDate.end || ''
      const startModifiedDate = modifiedDate?.start || ''
      const endModifiedDate = modifiedDate?.end || ''

      handleFilterTicketDates(
        startCreatedDate,
        endCreatedDate,
        startModifiedDate,
        endModifiedDate,
      )
    }

    if (modifiedDate?.start && modifiedDate?.end) {
      const startCreatedDate = createdDate?.start || ''
      const endCreatedDate = createdDate?.end || ''
      const startModifiedDate = modifiedDate.start || ''
      const endModifiedDate = modifiedDate.end || ''

      handleFilterTicketDates(
        startCreatedDate,
        endCreatedDate,
        startModifiedDate,
        endModifiedDate,
      )
    }

    if (
      createdDate?.start &&
      createdDate?.end &&
      modifiedDate?.start &&
      modifiedDate?.end
    ) {
      const startCreatedDate = createdDate.start || ''
      const endCreatedDate = createdDate.end || ''
      const startModifiedDate = modifiedDate.start || ''
      const endModifiedDate = modifiedDate.end || ''

      handleFilterTicketDates(
        startCreatedDate,
        endCreatedDate,
        startModifiedDate,
        endModifiedDate,
      )
    }

    if (status && status.length > 0) {
      switch (status[0]) {
        case 'FOR APPROVAL':
          handleFilterTicketStatus('FOR_APPROVAL')
          break
        case 'FOR REVISION':
          handleFilterTicketStatus('FOR_REVISION')
          break
        case 'REJECTED':
          handleFilterTicketStatus('REJECTED')
          break
        case 'CLOSED':
          handleFilterTicketStatus('CLOSED')
          break
        case 'FOR COC':
          handleFilterTicketStatus('FOR_COC_UPLOADING')
          break
        case 'FOR CONTRACTOR QUOTATION':
          handleFilterTicketStatus('FOR_CONTRACTOR_QUOTATION_UPLOADING')
          break
        case 'FOR PO QUOTATION':
          handleFilterTicketStatus('FOR_PO_QUOTATION_UPLOADING')
          break
        case 'FOR COC DATE COMPLETION':
          handleFilterTicketStatus('FOR_COC_DATE_AND_COMPLETION_DATE_INPUT')
          break
      }
    }

    if (methodOfRepair && methodOfRepair.length > 0) {
      handleFilterTicketMethodRepair(methodOfRepair[0])
    }

    if (typeOfConcern && typeOfConcern.length > 0) {
      handleFilterTicketTypeConcern(typeOfConcern[0])
    }

    onApplyFilter(currentFilters)

    onClose()
  }, [currentFilters, onApplyFilter, onClose, handleAlertMessage])

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose, initialFilters])

  const handleResetFilters = useCallback(() => {
    setCurrentFilters(createDefaultFilterState<T>()) // Reset to default values

    handleReset() // Call the parent reset function

    // Reset date filters
    handleFilterTicketDates('', '', '', '')

    // Reset method of repair and type of concern
    handleFilterTicketMethodRepair('')
    handleFilterTicketTypeConcern('')
    handleFilterTicketStatus('')
  }, [
    handleReset,
    handleFilterTicketDates,
    handleFilterTicketMethodRepair,
    handleFilterTicketTypeConcern,
  ])

  console.log('current filters ', currentFilters)

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          p: isMobile ? 2 : 4,
          m: isMobile ? 1 : 'auto',
          borderRadius: isMobile ? 0 : 2,
          height: isMobile ? '100%' : 'auto',
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Typography variant="h6" fontFamily="Gabarito">
          Filter By:
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ mt: 2 }}>
            {config.fields.map((fieldConfig) => (
              <UserFilterField
                key={fieldConfig.field}
                config={fieldConfig}
                filters={currentFilters}
                onFilterChange={handleUpdateFilter}
                onDateRangeChange={handleDateRangeChange}
                tab={tab}
                filterType={'ticket'}
              />
            ))}
          </Box>
        </LocalizationProvider>
      </DialogContent>

      <DialogActions
        sx={{
          p: 0,
          mt: 2,
          flexDirection: isMobile ? 'column-reverse' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? 1 : 0,
        }}
      >
        <Button
          onClick={handleResetFilters}
          sx={{
            color: 'text.secondary',
            textTransform: 'none',
            fontFamily: 'Gabarito',
          }}
        >
          Reset Filter
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          sx={{
            bgcolor: 'secondary.main',
            textTransform: 'none',
            fontFamily: 'Gabarito',
            '&:hover': {
              bgcolor: 'secondary.dark',
            },
          }}
        >
          Apply Filter
        </Button>
      </DialogActions>
    </Dialog>
  )
}
